import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageSwitcher from "@/components/atlas/LanguageSwitcher";

const LOGO_URL = "/atlas-logo.png";

export default function AtlasHeader({ onRequestDemo }) {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NAV = [
    { label: t("nav.soluciones"), href: "#soluciones", key: "soluciones" },
    { label: t("nav.casos"), href: "#casos-exito", key: "casos" },
    { label: t("nav.sectores"), href: "#sectores", key: "sectores" },
    { label: t("nav.recursos"), href: "#testimonios", key: "recursos" },
  ];

  const CLIENT_APP_URL = "http://app.atlas-system.net/";

  return (
    <header
      data-testid="atlas-header"
      className={`sticky top-0 z-50 w-full transition-all ${
        scrolled
          ? "bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-100"
          : "bg-white border-b border-slate-100"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
          <a href="#top" className="flex items-center gap-2" data-testid="atlas-logo" aria-label="ATLAS Industrial">
            <img
              src={LOGO_URL}
              alt="ATLAS Industrial"
              className="h-10 lg:h-12 w-auto object-contain select-none"
              draggable={false}
            />
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((item) => (
              <a
                key={item.key}
                href={item.href}
                data-testid={`nav-link-${item.key}`}
                className="text-[15px] font-medium text-slate-600 hover:text-[#6B46C1] transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <LanguageSwitcher />
            <a
              href={CLIENT_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-client-login-btn"
              className="inline-flex items-center h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:border-[#6B46C1] hover:text-[#6B46C1] text-sm font-semibold transition-colors"
            >
              {t("clientLogin")}
            </a>
            <Button
              data-testid="header-request-demo-btn"
              onClick={onRequestDemo}
              className="bg-[#6B46C1] hover:bg-[#553C9A] text-white rounded-lg px-5 h-10 font-semibold shadow-sm shadow-purple-200 transition-all"
            >
              {t("cta.requestDemo")}
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-1">
            <LanguageSwitcher compact />
            <button
              data-testid="mobile-menu-toggle"
              className="inline-flex items-center justify-center h-10 w-10 rounded-md text-slate-700 hover:bg-slate-100"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t("nav.openMenu")}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden pb-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1 pt-2 border-t border-slate-100">
              {NAV.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-3 rounded-md text-slate-700 hover:bg-slate-50 font-medium"
                >
                  {item.label}
                </a>
              ))}
              <a
                href={CLIENT_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-client-login-btn"
                onClick={() => setMobileOpen(false)}
                className="mt-2 h-10 flex items-center justify-center rounded-md border border-slate-300 text-slate-700 font-semibold text-sm hover:border-[#6B46C1] hover:text-[#6B46C1] transition-colors"
              >
                {t("clientLogin")}
              </a>
              <Button
                data-testid="mobile-request-demo-btn"
                onClick={() => {
                  setMobileOpen(false);
                  onRequestDemo();
                }}
                className="mt-2 bg-[#6B46C1] hover:bg-[#553C9A] text-white font-semibold"
              >
                {t("cta.requestDemo")}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
